import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Plus, Trash2, Search, UserCircle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';
import { ImportDialog } from '../components/ImportDialog';
import { CategoryBadge, CategoryFilter, CategoryPicker } from '../components/CategoryControls';
import { apiErrorMessage, lockedMasterCategory } from '../utils/requestTheme';


// Sama dengan Invoice, Purchase Order, Payment Request, dan Down Payment.
const PER_PAGE = 20;

export default function Recipients() {
  const { user } = useAuth();
  const locked = lockedMasterCategory(user);
  const [catFilter, setCatFilter] = useState('');
  const category = locked || catFilter;
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Kata kunci yang sudah dikirim ke server. Dipisah dari `searchTerm` supaya
  // setiap ketikan tidak memicu satu permintaan.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [formData, setFormData] = useState({
    nama: '',
    nomor_rekening: '',
    nama_bank: '',
    kode_bank: '',
    nomor_wa: '',
    email: '',
    alamat: '',
    kota: '',
    category: ''
  });

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
      if (appliedSearch) params.set('search', appliedSearch);
      if (category) params.set('category', category);
      const res = await api.get(`/recipients?${params.toString()}`);
      const data = res.data;
      setRecipients(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      // Halaman bisa melewati halaman terakhir bila ada yang terhapus di sesi
      // lain. Tanpa koreksi ini tabel diam-diam kosong padahal datanya ada.
      if ((data.items || []).length === 0 && page > (data.total_pages || 1)) {
        setPage(data.total_pages || 1);
      }
    } catch (error) {
      toast.error('Gagal memuat data penerima');
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, category]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  // Pencarian dikirim ke server, jadi di-debounce agar tiap ketikan tidak
  // jadi satu permintaan. Selalu kembali ke halaman 1: mencari dari halaman 5
  // tanpa reset bisa menghasilkan tabel kosong padahal hasilnya ada.
  useEffect(() => {
    const berikutnya = searchTerm.trim();
    if (berikutnya === appliedSearch) return undefined;
    const timer = setTimeout(() => {
      setAppliedSearch(berikutnya);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, appliedSearch]);

  const resetForm = () => {
    setFormData({
      nama: '',
      nomor_rekening: '',
      nama_bank: '',
      kode_bank: '',
      nomor_wa: '',
      email: '',
      alamat: '',
      kota: '',
      category: locked || catFilter || ''
    });
    setEditingRecipient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cat = locked || formData.category;
    if (!cat) return toast.error('Pilih kategori IPR atau OPR');
    const body = { ...formData, category: cat };
    try {
      if (editingRecipient) {
        await api.put(`/recipients/${editingRecipient.id}`, body);
        toast.success('Data penerima berhasil diupdate');
      } else {
        await api.post(`/recipients`, body);
        toast.success('Data penerima berhasil ditambahkan');
      }
      setShowForm(false);
      resetForm();
      fetchRecipients();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal menyimpan data'));
    }
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setFormData({
      nama: recipient.nama || '',
      nomor_rekening: recipient.nomor_rekening || '',
      nama_bank: recipient.nama_bank || '',
      kode_bank: recipient.kode_bank || '',
      nomor_wa: recipient.nomor_wa || '',
      email: recipient.email || '',
      alamat: recipient.alamat || '',
      kota: recipient.kota || '',
      category: recipient.category || 'ipr'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus data penerima ini?')) return;
    try {
      await api.delete(`/recipients/${id}`);
      toast.success('Data penerima berhasil dihapus');
      // Baris terakhir di halaman ini: mundur satu halaman, jangan tinggalkan
      // tabel kosong. setPage memicu pemuatan ulang lewat effect.
      if (recipients.length === 1 && page > 1) setPage(page - 1);
      else fetchRecipients();
    } catch (error) {
      toast.error('Gagal menghapus data');
    }
  };

  const canManage = user?.role === 'admin' || user?.role === 'approval';

  return (
    <Layout>
      <div className="space-y-6" data-testid="recipients-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Data Penerima</h1>
            <p className="text-slate-500 mt-1">Kelola data penerima untuk payment request</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cari nama/bank/rekening..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-recipient"
              />
            </div>
            <ExportMenu table="recipients" size="default" params={{ search: appliedSearch, category }} />
            {canManage && <ImportDialog type="recipients" title="Data Penerima" size="default" onDone={fetchRecipients} />}
            {canManage && (
              <Button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="add-recipient-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Tambah Penerima
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-purple-600" />
              Daftar Penerima
            </CardTitle>
            <CategoryFilter value={catFilter} onChange={(v) => { setCatFilter(v); setPage(1); }} locked={locked} testId="recipient-category-filter" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : recipients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-sm font-semibold">Nama</th>
                      <th className="px-4 py-3 text-sm font-semibold">No. Rekening</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama Bank</th>
                      <th className="px-4 py-3 text-sm font-semibold">Kode Bank</th>
                      <th className="px-4 py-3 text-sm font-semibold">No. WA</th>
                      <th className="px-4 py-3 text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-sm font-semibold">Kota</th>
                      <th className="px-4 py-3 text-sm font-semibold">Kategori</th>
                      {canManage && <th className="px-4 py-3 text-sm font-semibold">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((recipient) => (
                      <tr key={recipient.id} className="border-b" data-testid={`recipient-row-${recipient.id}`}>
                        <td className="px-4 py-3 font-semibold">{recipient.nama}</td>
                        <td className="px-4 py-3 font-mono text-sm">{recipient.nomor_rekening}</td>
                        <td className="px-4 py-3">{recipient.nama_bank}</td>
                        <td className="px-4 py-3 font-mono text-sm">{recipient.kode_bank || '-'}</td>
                        <td className="px-4 py-3">{recipient.nomor_wa || '-'}</td>
                        <td className="px-4 py-3 text-sm">{recipient.email || '-'}</td>
                        <td className="px-4 py-3">{recipient.kota || '-'}</td>
                        <td className="px-4 py-3"><CategoryBadge value={recipient.category} testId={`recipient-category-${recipient.id}`} /></td>
                        {canManage && (
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(recipient)}
                                data-testid={`edit-recipient-${recipient.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(recipient.id)}
                                data-testid={`delete-recipient-${recipient.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">
                {appliedSearch
                  ? `Tidak ada penerima yang cocok dengan "${appliedSearch}"`
                  : 'Belum ada data penerima'}
              </p>
            )}
            {!loading && total > 0 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                onPageChange={setPage}
              />
            )}
          </CardContent>
        </Card>

        {/* Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>
                {editingRecipient ? 'Edit Data Penerima' : 'Tambah Data Penerima'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="nama">Nama Penerima *</Label>
                  <Input
                    id="nama"
                    value={formData.nama}
                    onChange={(e) => setFormData({ ...formData, nama: e.target.value.toUpperCase() })}
                    required
                    data-testid="input-nama"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="nomor_rekening">No. Rekening *</Label>
                  <Input
                    id="nomor_rekening"
                    value={formData.nomor_rekening}
                    onChange={(e) => setFormData({ ...formData, nomor_rekening: e.target.value.toUpperCase() })}
                    required
                    data-testid="input-rekening"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="nama_bank">Nama Bank *</Label>
                  <Input
                    id="nama_bank"
                    value={formData.nama_bank}
                    onChange={(e) => setFormData({ ...formData, nama_bank: e.target.value.toUpperCase() })}
                    required
                    placeholder="Contoh: BANK BSI, PT. BANK CENTRAL ASIA Tbk."
                    data-testid="input-bank"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="kode_bank">Kode Bank</Label>
                  <Input
                    id="kode_bank"
                    value={formData.kode_bank}
                    onChange={(e) => setFormData({ ...formData, kode_bank: e.target.value.toUpperCase() })}
                    placeholder="Contoh: 451, CENAIDJA"
                    data-testid="input-kode-bank"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="nomor_wa">No. WhatsApp</Label>
                  <Input
                    id="nomor_wa"
                    value={formData.nomor_wa}
                    onChange={(e) => setFormData({ ...formData, nomor_wa: e.target.value })}
                    placeholder="08xxxxxxxxxx"
                    data-testid="input-wa"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
                    data-testid="input-email"
                    className="lowercase"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="alamat">Alamat</Label>
                  <Input
                    id="alamat"
                    value={formData.alamat}
                    onChange={(e) => setFormData({ ...formData, alamat: e.target.value.toUpperCase() })}
                    data-testid="input-alamat"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="kota">Kota</Label>
                  <Input
                    id="kota"
                    value={formData.kota}
                    onChange={(e) => setFormData({ ...formData, kota: e.target.value.toUpperCase() })}
                    placeholder="Contoh: MAKASSAR"
                    data-testid="input-kota"
                    className="uppercase"
                  />
                </div>
              </div>
              <CategoryPicker value={formData.category} onChange={(c) => setFormData({ ...formData, category: c })} locked={locked} testId="recipient-category" />
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Batal
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="submit-recipient">
                  {editingRecipient ? 'Update' : 'Simpan'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}



